import {
  DirectionsWalk as BaseIcon,
  FitnessCenter as HardIcon,
  Home as IndoorIcon,
  DirectionsRun as LongIcon,
  Park as OutdoorIcon,
  Bed as RestIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import type { ScheduleEvent, ScheduleEventLocation, ScheduleEventType } from '@shared/types';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';

interface EventEditorPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  existingEvent?: ScheduleEvent;
  onSave: (event: {
    eventType: ScheduleEventType;
    location?: ScheduleEventLocation;
    note?: string;
  }) => void;
  onDelete?: () => void;
  isGoalDate?: boolean;
  goalLabel?: string;
}

/**
 * Event type configuration with colors and icons
 */
export const EVENT_TYPE_CONFIG: Record<
  ScheduleEventType,
  { color: string; icon: React.ReactNode; label: string; emoji: string }
> = {
  rest: {
    color: '#9e9e9e',
    icon: <RestIcon fontSize="small" />,
    label: 'Rest Day',
    emoji: '🛌',
  },
  long: {
    color: '#2196f3',
    icon: <LongIcon fontSize="small" />,
    label: 'Long Training',
    emoji: '🏃',
  },
  hard: {
    color: '#f44336',
    icon: <HardIcon fontSize="small" />,
    label: 'Hard Training',
    emoji: '💪',
  },
  base: {
    color: '#4caf50',
    icon: <BaseIcon fontSize="small" />,
    label: 'Base/Easy',
    emoji: '🚶',
  },
};

export const LOCATION_CONFIG: Record<
  ScheduleEventLocation,
  { icon: React.ReactNode; label: string; emoji: string }
> = {
  indoor: {
    icon: <IndoorIcon fontSize="small" />,
    label: 'Indoor',
    emoji: '🏠',
  },
  outdoor: {
    icon: <OutdoorIcon fontSize="small" />,
    label: 'Outdoor',
    emoji: '🌳',
  },
};

/**
 * Popover editor for schedule events (used on desktop)
 * Provides quick editing of event type, location, and notes
 */
export const EventEditorPopover: React.FC<EventEditorPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  selectedDate,
  existingEvent,
  onSave,
  onDelete,
  isGoalDate,
  goalLabel,
}) => {
  const theme = useTheme();
  const [eventType, setEventType] = useState<ScheduleEventType | null>(
    existingEvent?.eventType ?? null
  );
  const [location, setLocation] = useState<ScheduleEventLocation | undefined>(
    existingEvent?.location
  );
  const [note, setNote] = useState(existingEvent?.note ?? '');

  // Reset state when opening for a new date
  useEffect(() => {
    if (open) {
      setEventType(existingEvent?.eventType ?? null);
      setLocation(existingEvent?.location);
      setNote(existingEvent?.note ?? '');
    }
  }, [open, existingEvent]);

  const handleSave = () => {
    if (eventType) {
      onSave({
        eventType,
        location,
        note: note.trim() || undefined,
      });
    }
    onClose();
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    onClose();
  };

  const handleClear = () => {
    setEventType(null);
    setLocation(undefined);
    setNote('');
    if (existingEvent && onDelete) {
      onDelete();
    }
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          sx: {
            p: 2,
            minWidth: 320,
            maxWidth: 360,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {selectedDate ? format(selectedDate, 'EEEE, MMM d') : 'Edit Event'}
        </Typography>
        {existingEvent && (
          <IconButton size="small" onClick={handleDelete} color="error" aria-label="Delete event">
            ✕
          </IconButton>
        )}
      </Box>

      {/* Goal Date Indicator */}
      {isGoalDate && (
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: alpha('#ffc107', 0.1),
            borderRadius: 1,
            border: `1px solid ${alpha('#ffc107', 0.3)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="body2">🏆 {goalLabel || 'Goal Race Day'}</Typography>
        </Box>
      )}

      {/* Event Type Selection */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Training Type
      </Typography>
      <ToggleButtonGroup
        value={eventType}
        exclusive
        onChange={(_, value) => value && setEventType(value)}
        aria-label="Select event type"
        sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
      >
        {(Object.keys(EVENT_TYPE_CONFIG) as ScheduleEventType[]).map(type => {
          const config = EVENT_TYPE_CONFIG[type];
          return (
            <ToggleButton
              key={type}
              value={type}
              aria-label={config.label}
              sx={{
                flex: 1,
                minWidth: 70,
                py: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                '&.Mui-selected': {
                  bgcolor: alpha(config.color, 0.2),
                  borderColor: config.color,
                  '&:hover': {
                    bgcolor: alpha(config.color, 0.3),
                  },
                },
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>{config.emoji}</span>
              <Typography variant="caption" sx={{ textTransform: 'none' }}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Typography>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>

      <Divider sx={{ my: 2 }} />

      {/* Location Selection (only if not rest day) */}
      {eventType && eventType !== 'rest' && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Location (optional)
          </Typography>
          <ToggleButtonGroup
            value={location}
            exclusive
            onChange={(_, value) => setLocation(value || undefined)}
            aria-label="Select location"
            sx={{ mb: 2, width: '100%' }}
          >
            {(Object.keys(LOCATION_CONFIG) as ScheduleEventLocation[]).map(loc => {
              const config = LOCATION_CONFIG[loc];
              return (
                <ToggleButton
                  key={loc}
                  value={loc}
                  aria-label={config.label}
                  sx={{
                    flex: 1,
                    py: 1,
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  <span>{config.emoji}</span>
                  <Typography variant="body2" sx={{ textTransform: 'none' }}>
                    {config.label}
                  </Typography>
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </>
      )}

      {/* Note field */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Note for AI Scheduler (optional)
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={2}
        placeholder="e.g., Group run, rainy weather expected, limited time..."
        value={note}
        onChange={e => setNote(e.target.value)}
        inputProps={{
          maxLength: 500,
          'aria-label': 'Note for scheduler',
        }}
        sx={{ mb: 2 }}
      />

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {existingEvent && (
          <Button variant="text" color="inherit" onClick={handleClear} aria-label="Clear event">
            Clear
          </Button>
        )}
        <Button variant="text" onClick={onClose} aria-label="Cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!eventType}
          aria-label="Save event"
        >
          Save
        </Button>
      </Box>
    </Popover>
  );
};

export default EventEditorPopover;
